import { notification, message } from 'ant-design-vue';

// 錯誤類型常數
export const ERROR_TYPES = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    BAD_REQUEST: 400,
    SERVER_ERROR: 500,
    GATEWAY_ERROR: 502,
    PAYLOAD_TOO_LARGE: 413,
    METHOD_NOT_ALLOWED: 405,
    REQUEST_TIMEOUT: 408,
    CONFLICT: 409,
    TOO_MANY_REQUESTS: 429,
    SERVICE_UNAVAILABLE: 503,
    GATEWAY_TIMEOUT: 504
};

// 錯誤訊息配置
export const ERROR_MESSAGES = {
    [ERROR_TYPES.UNAUTHORIZED]: {
        message: '未授權訪問',
        description: '請重新登入'
    },
    [ERROR_TYPES.BAD_REQUEST]: {
        message: '請求錯誤 400',
        description: '請求格式不正確，請檢查輸入內容'
    },
    [ERROR_TYPES.SERVER_ERROR]: {
        message: '請求錯誤 500',
        description: '伺服器發生錯誤，請聯絡管理員'
    },
    [ERROR_TYPES.GATEWAY_ERROR]: {
        message: '請求錯誤 502',
        description: '伺服器發生錯誤，請聯絡管理員'
    },
    [ERROR_TYPES.PAYLOAD_TOO_LARGE]: {
        message: '上傳檔案過大',
        description:
            '圖片或請求內容超過伺服器限制，請壓縮圖片後再試（建議小於 2MB）'
    },
    [ERROR_TYPES.METHOD_NOT_ALLOWED]: {
        message: '請求錯誤 405',
        description: '不支援的請求方法'
    },
    [ERROR_TYPES.REQUEST_TIMEOUT]: {
        message: '請求錯誤 408',
        description: '請求超時，請稍後重試'
    },
    [ERROR_TYPES.CONFLICT]: {
        message: '請求錯誤 409',
        description: '資源發生衝突，請重試'
    },
    [ERROR_TYPES.TOO_MANY_REQUESTS]: {
        message: '請求錯誤 429',
        description: '請求過於頻繁，請稍後再試'
    },
    [ERROR_TYPES.SERVICE_UNAVAILABLE]: {
        message: '請求錯誤 503',
        description: '服務暫時不可用，請稍後重試'
    },
    [ERROR_TYPES.GATEWAY_TIMEOUT]: {
        message: '請求錯誤 504',
        description: '閘道超時，請稍後重試'
    }
};

// 通用錯誤通知設定
const commonNotificationConfig = {
    duration: 5,
    style: {
        width: '400px'
    }
};

// 關閉所有 notification（用於在顯示 message 之前關閉 notification）
export const closeAllNotifications = () => {
    notification.destroy();
};

// 錯誤處理器
export const handleHttpError = (error, url = '', method = '', options = {}) => {
    let { skipNotification = false } = options;
    const status = error.response?.status;
    const apiMessage = error.response?.data?.message;

    // payment-status：查無交易／未付款為正常探測結果，不跳「請求錯誤 400」
    if (
        status === ERROR_TYPES.BAD_REQUEST &&
        typeof url === 'string' &&
        url.includes('/payment-status') &&
        typeof apiMessage === 'string' &&
        apiMessage.includes('查無交易資料')
    ) {
        skipNotification = true;
        error.message = apiMessage;
    }

    // 記錄錯誤
    console.error(`${method.toUpperCase()} ${url} API Error:`, error);

    const translateValidation = (payload) => {
        const isRecruitment =
            typeof url === 'string' && url.includes('/ice-fire/recruitments');

        const fieldLabels = {
            occurred_at: '發生時間',
            onboard_date: '起聘日',
            first_consult_date: '諮詢日',
            purchase_date: '購買日',
            completion_date: '完課日',
            referrer_user_id: '推薦人',
            mentor_user_id: '輔導人',
            new_partner_user_id: '新夥伴',
            user_id: '會員',
            student_user_id: '學員',
            consultee_name: '諮詢人姓名',
            student_name: '學員姓名',
            course_id: '課程'
        };

        // 頁面語意對齊：增員頁用「起聘日期」
        if (isRecruitment) {
            fieldLabels.occurred_at = '起聘日期';
            fieldLabels.onboard_date = '起聘日期';
        }

        const translateMessage = (raw, fieldKey) => {
            const field = fieldLabels[fieldKey] || fieldKey;
            const text = String(raw || '').trim();

            // Laravel 預設英文驗證訊息（只 cover 常見幾種）
            const patterns = [
                [/^The .* field is required\.$/i, `${field}為必填。`],
                [/^The .* field must be a date\.$/i, `${field}格式不正確。`],
                [
                    /^The .* field must be a date before or equal to now\.$/i,
                    `${field}不可晚於現在。`
                ],
                [
                    /^The .* field must be a date before or equal to today\.$/i,
                    `${field}不可晚於今天。`
                ],
                [/^The .* field must be a number\.$/i, `${field}格式不正確。`],
                [/^The selected .* is invalid\.$/i, `${field}不正確。`]
            ];
            for (const [re, mapped] of patterns) {
                if (re.test(text)) return mapped;
            }

            return text;
        };

        const errors = payload?.errors;
        if (!errors || typeof errors !== 'object') {
            return null;
        }

        const lines = [];
        for (const [fieldKey, messages] of Object.entries(errors)) {
            const list = Array.isArray(messages) ? messages : [messages];
            for (const msg of list.filter(Boolean)) {
                lines.push(translateMessage(msg, fieldKey));
            }
        }

        return lines.length ? lines[0] : null;
    };

    // 特殊錯誤處理
    if (status === ERROR_TYPES.UNAUTHORIZED) {
        const isAuthEndpoint =
            typeof url === 'string' &&
            (/\/login(?:\?|$)/.test(url) || /\/auth\//.test(url));

        // 登入失敗的 401 不要清 token / 強制整頁跳轉（會把「剛登入成功」衝掉）
        if (isAuthEndpoint) {
            error.message =
                error.response?.data?.message || '帳號或密碼錯誤';
            return Promise.reject(error);
        }

        // Token 過期或無效，強制登出
        localStorage.removeItem('accessToken');
        localStorage.removeItem('tokenType');
        localStorage.removeItem('expiresAt');
        localStorage.removeItem('adminInfo');

        if (!skipNotification) {
            notification.warning({
                message: '登入已過期',
                description: '請重新登入',
                duration: 5,
                style: { width: '400px' }
            });
        }

        // history mode：勿用 /#/login
        if (!window.location.pathname.endsWith('/login')) {
            const base = import.meta.env.BASE_URL || '/';
            window.location.assign(`${base}login`.replace(/\/{2,}/g, '/'));
        }
        return Promise.reject(error);
    }

    // 422 Validation Error：翻譯後用 toast 顯示，不跳通用 notification
    if (status === 422) {
        const translated = translateValidation(error.response?.data);
        const apiMessage =
            translated ||
            error.response?.data?.message ||
            '資料驗證失敗，請檢查輸入內容';
        if (!skipNotification) {
            message.error(apiMessage);
        }
        error.message = apiMessage;
        return Promise.reject(error);
    }

    if (status === ERROR_TYPES.FORBIDDEN) {
        // 無權限：用輕量 toast 提示即可，不需跳全屏錯誤，避免行政誤以為系統壞掉。
        const apiMessage =
            error.response?.data?.message || '您沒有執行此操作的權限';
        if (!skipNotification) {
            message.warning(apiMessage);
        }
        error.message = apiMessage;
        return Promise.reject(error);
    }

    if (status === ERROR_TYPES.PAYLOAD_TOO_LARGE) {
        error.message =
            error.response?.data?.message ||
            ERROR_MESSAGES[ERROR_TYPES.PAYLOAD_TOO_LARGE].description;
        // 413 多半發生在上傳流程，呼叫端會在表單內顯示更貼近情境的提示。
        return Promise.reject(error);
    }

    if (status === ERROR_TYPES.CONFLICT) {
        const apiMessage = error.response?.data?.message;
        if (apiMessage) {
            error.message = apiMessage;
        }
        // 409 為業務衝突，由呼叫端在表單／彈窗內顯示具體訊息，不再跳出「請求錯誤 409」
        return Promise.reject(error);
    }

    // 如果設定跳過 notification，就不顯示
    if (!skipNotification) {
        // 獲取錯誤配置
        const errorConfig = ERROR_MESSAGES[status] || {
            message: '請求錯誤',
            description: error.message || '發生未知錯誤，請稍後重試'
        };

        // 顯示錯誤通知
        notification.error({
            ...errorConfig,
            ...commonNotificationConfig
        });
    }

    // 對於伺服器錯誤，返回更詳細的錯誤資訊
    if (
        status === ERROR_TYPES.SERVER_ERROR ||
        status === ERROR_TYPES.GATEWAY_ERROR
    ) {
        return Promise.reject({
            status,
            message: error.response.data?.message || 'Server Error',
            error
        });
    }

    return Promise.reject(error);
};
